<template>
    <v-col v-if="classifiers_loaded" class="pb-0">
        <v-row no-gutters>
            <v-col class="mb-2">
                <ModernFilterBar
                    v-if="mobile_show_filterbar"
                    :filter-controller="filterController"
                >
                    <template v-slot:right="{filters, filterAdd}">
                        <AddFilterButton
                            :filters="filters"
                            @add_filter="filterAdd($event)"
                        />
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <icon-button icon="Delete" :on="on" @click="filterController.resetFilters()" />
                            </template>
                            {{ $t("Clear filters") }}
                        </v-tooltip>
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <icon-button
                                    icon="Save"
                                    :on="on"
                                    :disabled="$route.query && Object.keys($route.query).length < 1"
                                    @click="onSaveFiltersClick"
                                />
                            </template>
                            {{ $t("Save filters") }}
                        </v-tooltip>
                    </template>
                </ModernFilterBar>
            </v-col>
        </v-row>
        <v-row no-gutters>
            <v-col>
                <v-toolbar v-if="selected_account" flat class="medium-toolbar" color="transparent">
                    <v-tabs
                        v-model="tabIndex"
                        background-color="transparent"
                        class="denser-tabs"
                        slider-size="4"
                    >
                        <v-tab
                            v-for="(tab, tabindex) in visibleTabs"
                            :key="'tidx' + tabindex"
                            @change="changeTab(tab, tabindex)"
                        >
                            <span class="caption font-weight-medium">{{ tab.text }}</span>
                        </v-tab>
                    </v-tabs>
                    <v-spacer />
                    <v-btn v-if="projectEnabled" color="primary" text test-id="add_project_button" @click="addProjectClick">
                        <v-icon size="24" left>mdi-plus-box</v-icon>
                        {{ isEventTypeCompany(selected_account.id) ? $t('Add event') : $t('Add project') }}
                    </v-btn>
                    <v-btn
                        v-if="$vuetify.breakpoint.smAndDown"
                        small
                        text
                        class="text-none body-2 neutral_n300--text"
                        style="pointer-events: initial; z-index: 5;"
                        @click="mobile_show_filterbar = !mobile_show_filterbar"
                    >
                        {{ mobile_show_filterbar ? $t("Hide filters") : $t("Show filters") }}
                    </v-btn>
                    <ColumnController
                        :headers="richDataTableController.getAllHeaders()"
                        :visible-headers="richDataTableController.getVisibleHeaders().map(x => x.value)"
                        @update_columns="richDataTableController.setVisibleHeadersWithoutLoadData($event)"
                    />
                </v-toolbar>
            </v-col>
        </v-row>
        <v-row no-gutters>
            <v-col>
                <RichDataTable
                    :rich-data-table-controller="richDataTableController"
                >
                    <template v-slot:item.client_id="{ item }">
                        <div class="d-inline-flex align-center">
                            <router-link
                                :to="{ name: 'client_profile_projects', params: { id: item.client_id }}"
                                class="main--text text-decoration-none"
                            >
                                <ClientAvatar
                                    :color="item.client_id_disp.color"
                                    :initials="item.client_id_disp.client_initials"
                                    :size="32"
                                ></ClientAvatar>
                            </router-link>
                            <router-link
                                :to="{ name: 'client_profile_projects', params: { id: item.client_id }}"
                                class="main--text ml-2"
                            >
                                {{ item.client_id_disp && item.client_id_disp.name ? item.client_id_disp.name : '' }}
                            </router-link>
                        </div>
                    </template>
                    <template v-slot:item.edit="{ item }">
                        <v-btn v-if="canEddClient(item)" icon text color="grey" @click.stop="editProjectClick(item.id)">
                            <v-icon>edit</v-icon>
                        </v-btn>
                    </template>
                    <template v-slot:item.name="{ item }">
                        <div class="d-flex">
                            <icon icon="ClipboardList" :color="item.color" />
                            <div class="ml-2">
                                <router-link :to="`projects/${item.id}`">{{ item.name }}</router-link>
                            </div>
                        </div>
                    </template>
                    <template v-slot:item.workflow_state="{ item }">
                        <WorkflowStateRow
                            :project="item"
                            :state="item.workflow_state === undefined ? [] : item.workflow_state"
                        />
                    </template>
                    <template v-slot:default="{ item, header }">
                        {{ header.format(item, header) }}
                    </template>
                </RichDataTable>
            </v-col>
        </v-row>
    </v-col>
</template>

<script>
import { $t } from "@/i18n";
import AccountModule from "@/store/modules/account";
import AppModule from "@/store/modules/app";
import ColumnController from "../../components/ColumnController";
import {computed, onMounted, reactive, ref} from "@vue/composition-api";
import { getFieldFormatByType } from "@/composables/use-custom-fields";
import { useTable } from "@/composables/use-table";
import ProjectModule from "../../store/modules/project";
import ClientAvatar from "../../components/ClientAvatar.vue";
import AddFilterButton from "../../components/NewFilterBar/components/AddFilterButton";
import DialogModule from "../../store/modules/dialog";
import ClientModule from "@/store/modules/client";
import WorkflowStateRow from "../../components/Project/WorkflowStateRow";
import FilterBarController from "../../components/ModernFilterBar/FilterBarController";
import ModernFilterBar from "../../components/ModernFilterBar";
import Vue from "vue";
import UrlAdapter from "@/urlAdapter/urlAdapter";
import RichDataTableController from "../../components/RichDataTable/RichDataTableController";
import RichDataTable from "../../components/RichDataTable/RichDataTable";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import BookmarkModule from "@/store/modules/bookmark";

export default {
    name: "ProjectsList",
    components: {
        ModernFilterBar,
        ColumnController,
        ClientAvatar,
        AddFilterButton,
        WorkflowStateRow,
        RichDataTable
    },
    setup(props, ctx) {
        const bookmarkModule = BookmarkModule()
        const urlAdapter = new UrlAdapter()
        urlAdapter.loadDefaultBookmark('project', bookmarkModule.bookmarks.value)
        /** @type {FilterBarController} */
        const filterController = reactive(FilterBarController.init(urlAdapter))
        /** @type {RichDataTableController} */
        const richDataTableController = reactive(RichDataTableController.initAsync(urlAdapter))
        const options = [
            { key: "sortBy", default_value: "name" },
            { key: "sortDesc", default_value: false },
            { key: "itemsPerPage", default_value: 50 },
            { key: "page", default_value: 1 }
        ]
        richDataTableController.setDefaultOptions(options)

        const { available_company_accounts, getDefaultCompanyAccount, isFeatureEnabled } = AccountModule();
        const { fetchClassifiers, project_types, custom_fields, setPageTitle, rights_masks, showFeatureNotEnabled } = AppModule();
        const { table_footer_props, formatTableHeaderClass } = useTable();
        const { fetchProjects, isEventTypeCompany } = ProjectModule();
        const { setDialogOptions, openDialog } = DialogModule();
        const { fetchVisibleClients, clients } = ClientModule();

        const classifiers_loaded = ref(false);
        const projectType = ref(null);
        const tabIndex = ref(0);
        const mobile_show_filterbar = ref(true);
        const visibleTabs = computed(() => {
            let ret = [];
            if (projectType.value == null)
                return ret;
            ret.push({ view: "projects", text: isEventTypeCompany(selected_account.value.id) ? $t('Events') : $t('Projects'), name: "projects" });
            ret.push({ view: "overview", text: $t("Overview"), name: "overview" });
            return ret;
        });
        const availableAccounts = computed(() => {
            return available_company_accounts.value.filter(x => isFeatureEnabled(x.id, "project", false));
        });

        let active_account = getDefaultCompanyAccount(available_company_accounts.value.filter(x => isFeatureEnabled(x.id, "project", false)));
        const projectEnabled = computed(() => {
            if (selected_account.value == null)
                return false;
            return isFeatureEnabled(selected_account.value.id, "project", false);
        });


        const selectedAccount = computed(() => {
            let account_id_list = filterController.getFilterValueEntity('account_id')
            let account = null;
            if (Array.isArray(account_id_list) && account_id_list[0])
                account = account_id_list[0];
            else {
                account = availableAccounts.value[0]
            }

            return account
        })

        const selectedAccountId = computed(() => {
            return selectedAccount.value ? selectedAccount.value.id : null
        })

        const selected_account = computed(() => {
            let account_id_list = filterController.getFilterValueEntity('account_id');
            let account_id = null;
            if (Array.isArray(account_id_list) && account_id_list[0])
                account_id = account_id_list[0].id;
            if (!account_id) return null;
            return availableAccounts.value.find(x => x.id === account_id);
        });
        const viewHeaders = ref({});

        const getAccountCustomFields = () => {
            if (active_account == null) return [];
            if (custom_fields.value && custom_fields.value[active_account.id]) {
                return custom_fields.value[active_account.id].filter(cf => cf.related_type.startsWith("project."))
            } else {
                return []
            }
        }

        urlAdapter.addGetStateCallback(() => ({
            view: urlAdapter.getUrlParameter('view'),
        }))

        const beforeFilterInput = (params) => {
            filterController.resetFiltersOnAccountChange(params)

            let account_id_list = params['account_id'];
            let account_id = null;
            if (Array.isArray(account_id_list) && account_id_list[0])
                account_id = account_id_list[0].id;
            if (!account_id) return null;

            setHeaders(params)
        }

        richDataTableController.setLoadDataFunc((...a) => backendQuery(...a))
        filterController.setAfterFilterInputFunc((...a) => afterFilterInput(...a))
        filterController.setBeforeFilterInputFunc((...a) => beforeFilterInput(...a))

        const getMembers = computed(() => selectedAccount.value ? selectedAccount.value.members.filter(m => m.status === "active" && (m.type === "person" || m.type === "group")) : [])

        const loadData = async () => {
            if (urlAdapter.getUrlParameter('view') === 'overview')
                tabIndex.value = 1;

            let accountId = selectedAccountId.value;
            let data = {
                types: ["project_types", "custom_fields", "progress_steps"],
                account_id: accountId
            };
            try {
                await fetchClassifiers(data)

                setPageTitle(isEventTypeCompany(accountId) ? $t('Events') : $t('Projects'));

                if (project_types.value[accountId] !== undefined && project_types.value[accountId].length > 0) {
                    projectType.value = project_types.value[accountId][0];
                }
                else {
                    projectType.value = { title: $t("Project") };
                }
                if (!projectEnabled.value) {
                    showFeatureNotEnabled(selected_account.value);
                }
                setHeaders();
                classifiers_loaded.value = true;
            } catch (ex) {
                errorDialog.showError(ex)
            }
        };

        const setHeaders = (params) => {
            if (selected_account.value == null)
                return;

            if (!params) {
                params = urlAdapter.getUrlParameters()
            }

            //todo: duplicated
            let key = "project.{0}".format(projectType.value.id);
            let ret = custom_fields.value[selectedAccountId.value]
                .filter(x => {
                    if (x.related_type !== key)
                        return false;
                    if (x.type === "contacts" || x.type === "color" || x.type.startsWith("suppliers."))
                        return false;
                    return true;
                })
                .sort((a, b) => {
                    if (a.sort_order < b.sort_order) return -1;
                    if (a.sort_order > b.sort_order) return 1;
                    return 0;
                })
                .map(x => {
                    let col = {
                        value: x.name,
                        text: x.title,
                        format: getFieldFormatByType(x.type),
                        account_id: selectedAccountId.value,
                        is_listed: x.is_listed,
                    };
                    if (x._data && x._data.choices)
                        col.choices = x._data.choices;
                    if (x.type === "choice_multi" || x.type === "user_multi")
                        col.sortable = false;
                    return col;
                });
            if (params['view'] === 'overview') {
              ret.push({
                  text: "",
                  value: "workflow_state",
                  sortable: false,
                  align: "left",
                  format: getFieldFormatByType("text"),
                  is_listed: 1,
              });
            }
            ret.push({
                text: "",
                value: "edit",
                sortable: false,
                align: "right",
                width: 0,
                is_listed: 1,
            });

            richDataTableController.setAllHeaders(ret)
            if (params['view'] === 'overview') {
                filterController.initCustomParameters([
                    { key: "view", default_value: "overview" }
                ])
                richDataTableController.setVisibleHeadersWithoutLoadData(['name', 'workflow_state'])
            }
            else {
                richDataTableController.setVisibleHeadersWithoutLoadData(ret.filter(x => x.is_listed === 1).map(x => x.value))
                filterController.initCustomParameters([
                    { key: "view", default_value: "projects" }
                ])
            }
        };
        const backendQuery = async (params, disableLoad) => {
            if (disableLoad) {
                return false
            }

            if (!projectEnabled.value) {
                return;
            }

            if (!params) {
                params = urlAdapter.getUrlParameters()
            }

            const resp = await fetchProjects(params)

            richDataTableController.setRows(resp.items, resp.total_items)
        };
        const addProjectClick = () => {
            if (selected_account.value == null)
                return;
            ctx.root._router.push({
                name: "project_new",
                params: { account_id: selected_account.value.id, project_type_id: projectType.value.id },
            });
        };
        const editProjectClick = (project_id) => {
            ctx.root._router.push({
                name: "project_edit",
                params: { id: project_id },
            });
        };
        const openProjectClick = project_id => {
            ctx.root._router.push({
                name: "project_view",
                params: { id: project_id },
            });
        }
        const canEddClient = (item) => {
            return item.rights & rights_masks.value.manage_client === rights_masks.value.manage_client;
        }
        const onSaveFiltersClick = () => {
            setDialogOptions({
                component: () => import("@/views/Dialog/BookmarkDialog"),
                payload: {
                    type: "project"
                }
            });
            openDialog();
        };
        const changeTab = (tab, tabindex) => {
            if (selected_account.value == null)
                return;
            if (tabIndex.value !== tabindex) {
                let oldKey = "{0}-{1}".format(visibleTabs.value[tabIndex.value].name, selected_account.value.id);
                let newKey = "{0}-{1}".format(tab.name, selected_account.value.id);

                Vue.set(viewHeaders.value, oldKey, filterController.getValue('columns'));
                tabIndex.value = tabindex;

                let params = filterController.getCurrentFilterValues()

                params['view'] = tab.name

                if (viewHeaders.value[newKey] !== undefined) {
                    params['columns'] = viewHeaders.value[newKey]
                }

                setHeaders(params);
                urlAdapter.updateUrlParameters()
            }
        }

        const afterFilterInput = function(filter_key, value){
            if (['account_id'].includes(filter_key)) {
                loadData();
            }
        }

        onMounted(async () => {
            if (urlAdapter.getUrlParameter('view') === 'overview') {
                tabIndex.value = 1;
            }

            await fetchVisibleClients()

            filterController.initFilters(
                [
                    { key: "account_id", default_value: selectedAccountId.value ? selectedAccountId.value : [], items: availableAccounts, default: true },
                    { key: "client_id_list", default_value: [], items: clients },
                    { key: "project_status", default_value: ["1", "2"], default: true },
                    { key: "keyword", default_value: [""], default: true },
                    { key: "custom_field", default_value: [], items: getAccountCustomFields, members: getMembers.value, repeatable: true },
                ]
            )

            filterController.startUrlWatcherWithLoadData();
            await loadData()
            if (urlAdapter) {
                richDataTableController.loadUrlState()
            }
            await filterController.persistCurrentFilterStateToUrl()
        })

        return {
            /** @type {FilterBarController} */
            filterController,
            projectType,
            selected_account,
            table_footer_props,
            formatTableHeaderClass,
            classifiers_loaded,
            projectEnabled,
            tabIndex,
            visibleTabs,
            mobile_show_filterbar,
            addProjectClick,
            editProjectClick,
            openProjectClick,
            canEddClient,
            onSaveFiltersClick,
            changeTab,
            /** @type {RichDataTableController} */
            richDataTableController,
            isEventTypeCompany
        };
    }
}
</script>
